import type { CalendarChatJson } from "@mirage/mosaics/Chat/components/chat/calendar/types";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { SearchParams } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import type {
  ModelSource,
  ResponseFormat,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";
import { ConversationsAgent } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";
import type {
  FSSFile,
  FSSFolder,
} from "@mirage/service-fss-bridge/service/bridge_types";

export { ModelSource } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";

export type ExperimentSetting = "CONTROL" | "V1" | "V2" | "V3";

// base type for all NewMessage arguments
interface NewMessageBase {
  conversationId: string;
  model?: ModelId;
  agentPayload?: string;
  attachments?: DocumentInfo[];
  agentIds?: AgentId[];
  clientContext?: ConversationClientContext;
  /**
   * @deprecated Use `searchParams` instead
   */
  skipSearch?: boolean;
  source?: string;
  searchParams?: SearchParams;
  skipPersistence?: boolean;
  fssSearchScope?: string;
  /**
   * Used to scope a conversation to a specific stack.
   * Should be the stack's namespace ID in the format of "ns:<namespace-id>"
   */
  stackId?: string;
}

/**
 * Arguments for posting a new user message.
 */
export interface NewUserMessage extends NewMessageBase {
  message: string;
  llmIgnore?: boolean;
  rephrased?: string;
  messageType?: "userMessage" | "assistantMessage";
  /**
   * Tool responses to append to the history prior to the user message.
   */
  toolResponses?: ConversationToolMessage[];
}

/**
 * Arguments for trigger a new response from server due to tool responses.
 * i.e. no new user message, just tool responses.
 */
export interface NewToolResponsesMessage extends NewMessageBase {
  messageType: "toolResponses";
  toolResponses: ConversationToolMessage[];
}

export type MessageStatus = "success" | "pending" | "streaming" | "error";

export type UserMessageOptions = {
  /**
   * @deprecated Use `searchParams` instead
   */
  skipSearch?: boolean;
  searchParams?: SearchParams;
  hideFollowUpSuggestions?: boolean;

  /**
   * IDs identifying specific agents that may respond to this message.
   */
  agentIds?: AgentId[];

  /**
   * Persist agentIds on new message submissions.
   * By default (false) agentIds will be reset after each new message response.
   */
  persistAgentIds?: boolean;

  /**
   * Controls whether the conversation should be persisted to the backend.
   */
  skipPersistence?: boolean;

  /**
   * Config for additional context ingested from the browser.
   */
  browserContext?: {
    /**
     * IDs of the tabs that the user has selected as content to ingest.
     */
    selectedTabIDs: Set<number>;
  };

  /**
   * Pre-defined clientContext value to use on next message submission.
   */
  clientContext?: ConversationClientContext;

  /**
   * Used to scope fss search conversations to a specific path in the fss.
   */
  fssSearchScope?: string;

  /**
   * Used to scope a conversation to a specific stack.
   * Should be the stack's namespace ID in the format of "ns:<namespace-id>"
   */
  stackId?: string;
};

export interface IconResource {
  lightUrl: string;
  darkUrl: string;
  lightPngUrl: string;
  darkPngUrl: string;
}

export interface Author {
  display_name: string;
  email: string;
  profile_image_url: string;
  alias_handle: string;
  profile_link: string;
}

export type ChannelType =
  | { ".tag": "channel_type_unset" }
  | { ".tag": "channel_type_public" }
  | { ".tag": "channel_type_private" }
  | { ".tag": "channel_type_dm" }
  | { ".tag": "other" };

export interface Channel {
  id: string;
  name: string;
  url: string;
  created_at_ms: number;
  members?: Author[];
  type: ChannelType;
}

/**
 * These types are very similar to the types returned by the apiv2
 * endpoint as well as the GRCP streaming endpoint. But they are not
 * exactly the same, so this serves as the contract for this service.
 * If there are API updates in the future, we can decide to update this
 * type or try and populate new fields from existing data. It was a pain
 * updating this service to the streaming endpoint when the types were tied
 * to the apiv2 types.
 */
export interface DocumentInfo {
  url: string;
  title: string;
  // can't use bigint here because our KV storage doesn't support it
  lastUpdatedMs: number;
  brandedType: string;
  body: string;
  ownerId: string;
  thirdPartyId: string;
  relevanceScore: number;
  connectorInfo?: {
    connectorId: string;
    connectorType: number;
    displayName: string;
    platform: number;
    connectorName: string;
    connectorIconUrl: string;
    icon?: IconResource;
  };
  uuid: string;
  index: string;
  fileTypeInfo?: {
    id: string;
    displayName: string;
    icon?: IconResource;
  };
  transient?: boolean;
  channel?: Channel;
  citationType?: string;
  retrievalSource?: string;
  fssRetrieverInfo?: {
    fssFileIdentifier: {
      fileId: string;
    };
  };
  previewUrl?: string;
  inclusionType?: string;
}

export type ProgressUpdate = {
  step: ProgressUpdateStep;
  context: DocumentInfo[];
  agentId: string;
};

export enum ProgressUpdateStep {
  STEP_SEARCH = 0,
  STEP_CONTEXT_SELECTION = 1,
  STEP_RESPONSE_GENERATION_FROM_LLM = 2,
  STEP_RESPONSE_GENERATION_FROM_AGENT = 3,
  STEP_SUGGESTED_QUERIES_GENERATION = 4,
  STEP_INTENT_DETECTION = 5,
}

export interface ConversationSystemMessage {
  case: "systemMessage";
  value: {
    text: string;
  };
}

export interface ConversationUserMessage {
  case: "userMessage";
  value: {
    text: string;
    rephrased: string;
    attachments: DocumentInfo[];
    agentPayload?: string;
    fssSearchScope?: string;
    llmIgnore?: boolean;
  };
}

export interface ConversationMessageJsonContent {
  content: string;
  followUpSuggestions: string[];
  draftModifications: string;
  calendarEvents: CalendarChatJson[];
}

export interface ConversationAssistantMessage {
  case: "assistantMessage";
  value: {
    text: string;
    cited: DocumentInfo[];
    context?: DocumentInfo[];
    isCached: boolean;
    agentId?: string;
    format?: ResponseFormat;
    toolCalls?: ToolCall[];
  };
}

export interface ConversationToolMessage {
  case: "toolMessage";
  value: {
    text: string;
    toolCallId: string;
    name: string;
    content: string;
    arguments?: string[];
  };
}

export interface ConversationUndefinedMessage {
  case: undefined;
  value?: undefined;
}

type ConversationMessageType =
  | ConversationSystemMessage
  | ConversationUserMessage
  | ConversationAssistantMessage
  | ConversationToolMessage
  | ConversationUndefinedMessage;

/**
 * This is the same type as ConversationsChatMessage, which is what the API
 * returns, with the exception of 'timestamp' and 'lastUpdatedMs' which have to be
 * converted to a Date from BigInt because KVStore does not support the latter,
 * as well as the addition of a few fields that are not in the API response.
 */
export interface ConversationMessage<
  T extends ConversationMessageType = ConversationMessageType,
> {
  id: string;
  messageType: T;

  // Converted from BigInt to Date
  timestamp?: Date;

  // Additional client fields
  status: MessageStatus;
  jsonContent?: ConversationMessageJsonContent;
}

export interface ConversationSuggestedQuery {
  text: string;
  docIds: string[];
}

export interface Conversation {
  messages: ConversationMessage[]; // sorted by most recent last
  suggestedQueries: ConversationSuggestedQuery[]; // updated after each server response
  markdownDraft?: string;
  progressUpdates?: ProgressUpdate[]; // Changed to full ProgressUpdate objects with context
  serverConversationId: string | undefined; // set after first response from the server
  writeAgentPayload?: string; // write agent payload from UserConversation.draft.write_agent_payload
  clientContext?: ConversationClientContext;
  assistRequestId?: string;
  spanId?: string; // used for tracing
  stackId?: string; // A stack's namespace ID, if the conversation is scoped to a stack
}

// Note: more explicit type for client since the backend is typed as a string
export type ConversationClientScenario =
  | "stack"
  | "workflow_agent"
  | "suggested_query"
  | "serp-answer"
  | "serp-follow-up"
  | ConversationFSSClientScenario;

export const ANSWERS_CLIENT_SCENARIOS = [
  "serp-answer",
  "fss-serp-answer",
] as const;

export type ConversationAnswersClientScenario =
  (typeof ANSWERS_CLIENT_SCENARIOS)[number];

export const FSS_CLIENT_SCENARIOS = [
  "fss-unscoped",
  "fss-suggested-query",
  "fss-serp-answer",
  "fss-serp-follow-up",
  "fss-file",
  "fss-file-preview",
  "fss-folder",
  "fss-stack",
] as const;

export type ConversationFSSClientScenario =
  (typeof FSS_CLIENT_SCENARIOS)[number];

export type ConversationFSSClientScenarioSources =
  | FSSFile[]
  | FSSFolder
  | FSSFile;

// Note: use `createConversationClientContext()` function when creating a new ClientContext
// as clientScenario is typed as a string on the backend, but the client can ensure type safety
// with the ConversationClientScenario type
export interface ConversationClientContext {
  timezone: string;
  clientScenario: string; // maps to ConversationClientScenario on the client
  clientScenarioId: string;
}

// Conversation type with a local conversation ID (returned by methods that may create a new conversation)
export interface ConversationWithId extends Conversation {
  id: string; // local conversation ID
}

/**
 * Server metadata for a conversation.
 */
export interface ConversationMetadata {
  serverConversationId: string;
  created: Date | string;
  updated: Date | string;
  title: string;
  initialAgent?: string;
  preview: string;
  clientContext?: ConversationClientContext;
  stackId?: string; // A stack's namespace ID, if the conversation is scoped to a stack
}

// TODO: move non-conversation types to separate files

export interface WorkflowAgent {
  id: string;
  dataId?: string;
  name: string;
  description: string;
  type: AgentId;
  model: string;
  sources: string[];
  sourceContents: ChatSource[];
  allowCustomSources?: boolean;
  icon: string;
  theme: string;
  workflow: WorkflowValues;
  createdBy: {
    display_name: string;
    profile_photo_url: string | undefined;
    email: string;
  };
  createdAtMs: number;
  updatedAtMs: number;
  accessType: "private" | "company";
}

export interface WorkflowValues {
  [step: number]: WorkflowStepValuesType;
}

export type WorkflowStepValuesType = {
  [key: string]: string | boolean | string[] | WorkflowStepValuesType;
};

export type WorkflowAgentRouteState = {
  agentId: string;
  documentIds: string[];
  model: ModelId;
};

export type ModelId = string;

// Represents a model that is available for use in conversations
export interface ModelInfo {
  // an opaque identifier for the model, used for model lookup
  // this is often a combination of provider and model name: ex: "openai:gpt-4"
  id: ModelId;

  // Human-readable name of the model to display in UI. ex: gpt-4o (OpenAI), Pixtral (Groq)
  displayName: string;

  // Brief description of the model's capabilities or recommended use cases
  description: string;

  // Indicates whether the model is certified for use
  // Certified models have been evaluated for quality and accuracy
  // Non-certified models may be less reliable and should be considered experimental
  isCertified: boolean;

  // The source of the model (e.g. DROPBOX, OPENAI, GROQ, etc.)
  modelSource: ModelSource;
}

export interface AvailableModels {
  models: ModelInfo[];
}

// NOTE: AgentId are the client-side and are what is used in dash repo
// These __should__ map to ConversationsAgent but mismatch in types are due to
// proto buff defs from rserver not being updated into this repo
export type AgentId =
  | "conversations"
  | "write"
  | "summarize"
  | "rewrite"
  | "fss_ops";

export const mapConversationsAgentToAgentId: {
  [key in ConversationsAgent]?: AgentId[];
} = {
  [ConversationsAgent.WRITE]: ["write"],
  [ConversationsAgent.CONVERSATION]: ["conversations"],
  [ConversationsAgent.SUMMARIZE]: ["summarize"],
};

// Agent ID constants
export const CALENDAR_AGENT_ID = "fs:dash-calendar-agent";

export type ConversationGroup = {
  title: string;
  conversations: ConversationMetadata[];
};

// Equivalent to ConversationsToolCall in grpc
export interface ToolCall {
  /**
   * The ID of the tool call
   */
  id: string;

  /**
   * The name of the tool to be called
   */
  name: string;

  /**
   * The arguments to call the tool with, as generated by the model in JSON format.
   * Note that the model does not always generate valid JSON, and may hallucinate parameters not
   * defined by your function schema. Validate the arguments in your code before calling your function.
   */
  arguments: string;

  /**
   * The type of the tool. Currently only `function` is supported.
   */
  type: string;
}
